import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LOGO_URL } from '../mock';

const PageLoader = () => {
  const [show, setShow] = useState(true);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(0); // 0: reveal, 1: progress, 2: exit

  useEffect(() => {
    const st = setTimeout(() => setStage(1), 500);
    let v = 0;
    const step = setInterval(() => {
      v = Math.min(100, v + Math.random() * 12 + 6);
      setProgress(v);
      if (v >= 100) {
        clearInterval(step);
        setTimeout(() => setStage(2), 500);
        setTimeout(() => setShow(false), 1400);
      }
    }, 150);
    return () => { clearInterval(step); clearTimeout(st); };
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="loader-root"
          className="fixed inset-0 z-[100] overflow-hidden pointer-events-none"
        >
          {/* Split curtain reveal */}
          <motion.div
            initial={{ y: 0 }}
            animate={{ y: stage >= 2 ? '-100%' : 0 }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: stage >= 2 ? 0.05 : 0 }}
            className="absolute inset-x-0 top-0 h-1/2 bg-[#0f0d0b]"
          />
          <motion.div
            initial={{ y: 0 }}
            animate={{ y: stage >= 2 ? '100%' : 0 }}
            transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
            className="absolute inset-x-0 bottom-0 h-1/2 bg-[#0f0d0b]"
          />

          {/* Golden seam line */}
          <motion.div
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: stage >= 2 ? 1 : 0, opacity: stage >= 2 ? 1 : 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: stage >= 2 ? 0.05 : 0 }}
            className="absolute top-1/2 left-0 right-0 h-[1.5px] bg-gradient-to-r from-transparent via-[#f0c95a] to-transparent origin-center"
          />

          {/* Content */}
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: stage >= 2 ? 0 : 1 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 grid place-items-center text-white"
          >
            {/* Ambient grid */}
            <div className="absolute inset-0 opacity-[0.08]" style={{
              backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
              backgroundSize: '80px 80px',
            }} />
            <motion.div
              animate={{ opacity: [0.15, 0.45, 0.15] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute inset-0"
              style={{ background: 'radial-gradient(circle at 50% 45%, #c8942a55, transparent 55%)' }}
            />

            <div className="relative text-center px-6 w-full max-w-2xl">
              {/* Emblem */}
              <motion.div
                initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-28 h-28 mx-auto"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                  className="absolute inset-0 rounded-full border border-dashed border-[#c8942a]/40"
                />
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                  className="absolute inset-1 rounded-full border-2 border-transparent border-t-[#f0c95a] border-r-[#c8942a]"
                />
                <img src={LOGO_URL} alt="WBS" className="absolute inset-4 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              </motion.div>

              {/* Portal name — split reveal */}
              <div className="mt-10 overflow-hidden">
                <motion.div
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className="font-serif-display text-[12px] md:text-[15px] tracking-[0.5em] uppercase text-[#f0c95a] font-black"
                >
                  {'WHITEPLAINS'.split('').map((c, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + i * 0.05, duration: 0.5 }}
                      className="inline-block"
                    >
                      {c === ' ' ? '\u00a0' : c}
                    </motion.span>
                  ))}
                </motion.div>
              </div>

              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.9, duration: 0.7 }}
                className="gold-divider my-5 mx-auto max-w-xs origin-center"
              />

              <div className="overflow-hidden">
                <motion.div
                  initial={{ y: 40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.7, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="font-serif-display text-2xl md:text-4xl font-black tracking-wide text-white"
                >
                  British School
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1 }}
                className="font-cormorant italic text-white/70 text-lg mt-2"
              >
                Cambridge Pathway · Bonitas et Disciplina
              </motion.div>

              {/* Progress */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: stage >= 1 ? 1 : 0, y: 0 }}
                transition={{ delay: 1.2, duration: 0.5 }}
                className="mt-12 max-w-md mx-auto"
              >
                <div className="flex items-center justify-between text-[10px] tracking-[0.35em] uppercase text-white/55 mb-3">
                  <motion.span
                    key={Math.floor(progress / 25)}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {progress < 25 ? 'Initialising' : progress < 55 ? 'Loading Assets' : progress < 85 ? 'Composing Interface' : 'Almost Ready'}
                  </motion.span>
                  <span className="font-serif-display text-white text-sm font-black tabular-nums">{String(Math.round(progress)).padStart(3, '0')}%</span>
                </div>
                <div className="h-[2px] w-full bg-white/10 overflow-hidden relative">
                  <motion.div
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className="h-full bg-gradient-to-r from-[#c8942a] to-[#f0c95a] relative"
                  >
                    <motion.span
                      animate={{ x: ['-100%', '100%'] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                    />
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PageLoader;
